import { TFunction } from 'i18next';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getCurrentLangPretty } from '../../../helpers/url/urlHelpers';
import usePostalServices from '../../../hooks/usePostalServices';
import useScrollToFirstFormikError from '../../../hooks/useScrollToFIrstFormikError';
import { ReactComponent as IconMailOpen } from '../../../icons/mail-open.svg';
import { ReactComponent as IconParcel } from '../../../icons/parcel.svg';
import { IFormSelectOption } from '../../../interfaces/forms/forms';
import IPostService from '../../../interfaces/forms/postService';
import Button from '../../base/button';
import { FormikFieldWithUnit } from '../../base/forms/formikFieldWithUnit';
import FormikFieldWrapper from '../../base/forms/formikFieldWrapper';
import FormikNumberInput from '../../base/forms/formikNumberInput';
import FormikRadioTile from '../../base/forms/formikRadioTile/formikRadioTile';
import FormikSelect from '../../base/forms/formikSelect';
import { Label } from '../../base/forms/label';
import { Column, Columns } from '../../base/grid';
import { A, P } from '../../base/typography';
import Services from '../../registerShipment/registerShipmentForm/stepShipment/stepShipmentForm/components/services';
import { IRegisterShipmentCalculator } from '../registerShipmentCalculator.schema';
import CalcCountrySelection from './calcCountrySelection/calcCountrySelection';
import * as StyledForm from './calcForm.styles';
import CalcPackageDimensions from './calcPackageDimensions/calcPackageDimensions';

interface Props {
	values: IRegisterShipmentCalculator;
	setFieldTouched: any;
	setFieldValue: any;
	isValid: boolean;
	isSubmitting: boolean;
	errors: any;
}

const CalcForm: FC<Props> = ({ values, setFieldTouched, setFieldValue, errors, isSubmitting, isValid }) => {
	const { t } = useTranslation('registerShipment') as unknown as { t: TFunction };
	const ref = useRef(null);
	const isIcelandSelected = values.country.value === 'IS';
	useScrollToFirstFormikError(errors, isSubmitting, isValid, ref);

	const { services, servicesLoading }: { services: IPostService[]; servicesLoading: boolean } = usePostalServices(
		values.parcelWeight,
		values.service,
		values.country,
		values.fromPostalCode,
		values.toPostalCode,
		values.parcelLength,
		values.parcelWidth,
		values.parcelHeight
	);
	const [letterWeight, setLetterWeight] = useState<IFormSelectOption>(null);
	const foreignLetterOptions: IFormSelectOption[] = [
		{ value: '49', label: '0-50g' },
		{ value: '99', label: '51-100g' },
		{ value: '249', label: '101-250g' },
		{ value: '499', label: '251-500g' },
		{ value: '999', label: '501-1000g' },
		{ value: '1499', label: '1001-1500g' },
		{ value: '1999', label: '1501-2000g' },
	];

	useEffect(() => {
		let service = null;
		if (services && values.serviceOption) {
			service = services.find(item => item.id === values.serviceOption);
			if (service === undefined) {
				setFieldValue('serviceName', null);
				setFieldValue('price', '');
			} else if (values.serviceName !== service.name) {
				setFieldValue('serviceName', service.name);
				setFieldValue('price', service.priceWithoutCommas);
			} else if (values.price !== service.priceWithoutCommas) {
				setFieldValue('price', service.priceWithoutCommas);
			}
		}
	}, [services, values, setFieldValue]);

	const disabled = isIcelandSelected
		? !values.service ||
		  !values.parcelWeight ||
		  !values.serviceOption ||
		  !values.toPostalCode ||
		  !values.fromPostalCode
		: !values.service || !values.parcelWeight || !values.serviceOption;

	return (
		<div ref={ref}>
			<StyledForm.Wrapper>
				<Columns>
					<Column desktopSmall={2 / 3}>
						<CalcCountrySelection
							values={values}
							isIcelandSelected={isIcelandSelected}
							setFieldTouched={setFieldTouched}
							setFieldValue={setFieldValue}
						/>
						<FormikFieldWrapper
							label={`${t('step-type.what-are-you-delivering')}?`}
							name="service"
							marginBottom="6rem"
							languageComponent="registerShipment">
							<FormikRadioTile
								id="Letter"
								setFieldValue={setFieldValue}
								name="service"
								icon={IconMailOpen}
								active={false}
								label={t('step-type.registered-letter')}
							/>
							<FormikRadioTile
								id="Parcel"
								name="service"
								setFieldValue={setFieldValue}
								icon={IconParcel}
								active={false}
								label={t('step-type.parcel')}
								renderTopRight={() => (
									<FormikNumberInput
										id="parcelCount"
										name="parcelCount"
										onChange={setFieldValue}
										value={!isIcelandSelected ? 1 : values.parcelCount}
										disabled={!isIcelandSelected}
										disabledText={t('step-type.parcel-disabled-text')}
										label={`${t('step-type.number-of-parcels')}`}
									/>
								)}
							/>
						</FormikFieldWrapper>
						{values.service && (
							<StyledForm.WeightAndDimensionWrapper>
								<Label marginBottom="2rem">{t('step-shipment.choose-weight')}</Label>
								{/*<WeightInfo />*/}
								<Grid>
									{!isIcelandSelected && values.service === 'Letter' ? (
										<FormikSelect
											id={'parcelWeight'}
											options={foreignLetterOptions}
											placeholder={t('step-shipment.weight')}
											onChange={(key: string, value: IFormSelectOption) => {
												setLetterWeight(value);
												setFieldValue(key, value.value);
											}}
											onBlur={setFieldTouched}
											name={'parcelWeight'}
											value={letterWeight}
										/>
									) : (
										<FormikFieldWrapper
											name="parcelWeight"
											label={t('step-shipment.weight')}
											smallLabel
											languageComponent="registerShipment"
											marginBottom="2rem">
											<FormikFieldWithUnit
												id="parcelWeight"
												setFieldValue={setFieldValue}
												name="parcelWeight"
												type="number"
												step={values.service === 'Parcel' ? 0.5 : 1}
												min={values.service === 'Parcel' ? 0.5 : 0}
												value={values.parcelWeight}
												placeholder={t('step-shipment.weight')}
												unit={values.service === 'Parcel' ? 'kg' : 'gr'}
											/>
										</FormikFieldWrapper>
									)}
									<P>
										{values.service === 'Letter' ? (
											<Trans
												t={t}
												i18nKey={'step-shipment.max-letter-weight'}
												components={[
													<A
														target={'_blank'}
														href={
															getCurrentLangPretty() === 'is'
																? '/einstaklingar/ymsar-upplysingar/verdskra/'
																: '/en/individuals/information/rates/'
														}
													/>,
												]}
											/>
										) : (
											t('step-shipment.round-up-weight')
										)}
									</P>
								</Grid>
								{values.service === 'Parcel' && (
									<>
										<div style={{ width: '50%', paddingRight: '1.5rem' }}>
											<Label marginBottom="2rem">{t('step-shipment.size-matters')}</Label>
											<P>{t('step-shipment.enter-weight-description')}</P>
										</div>
										<CalcPackageDimensions
											height={values.parcelHeight}
											width={values.parcelWidth}
											length={values.parcelLength}
											alwaysOpen
										/>
									</>
								)}
							</StyledForm.WeightAndDimensionWrapper>
						)}

						{(services || servicesLoading) && (
							<StyledForm.ServicesWrapper>
								<Label>{t('calculator.choose-service')}</Label>
								<Services
									services={services}
									loading={servicesLoading}
									values={values}
									setFieldTouched={setFieldTouched}
									setFieldValue={setFieldValue}
									errors={errors}
								/>
							</StyledForm.ServicesWrapper>
						)}
						{/* Not using optional services here at the moment */}
						{/* {((optionalServices && optionalServices.length > 0) || optionalServicesLoading) && (
							<StyledForm.ServicesWrapper>
								<Label>{t('step-optional-services.choose-optional-service')}</Label>
								<OptionalServices
									services={optionalServices}
									loading={optionalServicesLoading}
									values={values}
									setFieldTouched={setFieldTouched}
									setFieldValue={setFieldValue}
									errors={errors}
								/>
							</StyledForm.ServicesWrapper>
						)} */}
						{/* <CalcResults values={values} services={services} optionalServices={[]} /> */}
						{!disabled && (
							<StyledForm.ButtonWrapper>
								<Button wide primary mobileBlock type="submit">
									{t('calculator.register-shipment')}
								</Button>
							</StyledForm.ButtonWrapper>
						)}
					</Column>
				</Columns>
			</StyledForm.Wrapper>
		</div>
	);
};

const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 3rem;
`;

export default CalcForm;
